import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';
import { getStoredToken, setStoredToken, removeStoredToken } from '../utils/storage';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [availableTenants, setAvailableTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const token = getStoredToken();
            if (token) {
                const userData = await authService.getCurrentUser();
                setUser(userData);

                // Wenn User bereits einen Tenant ausgewählt hat, lade ihn
                const storedTenant = localStorage.getItem('selectedTenant');
                if (storedTenant) {
                    setSelectedTenant(JSON.parse(storedTenant));
                }

                // Verfügbare Tenants vom Backend laden
                await loadAvailableTenants(userData);
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            removeStoredToken();
            localStorage.removeItem('selectedTenant');
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableTenants = async (userData) => {
        try {
            // Prüfe ob wir MOCK API verwenden
            const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true';

            if (USE_MOCK_API) {
                // Mock-Tenants für Development
                const mockTenants = [
                    {
                        id: 'tenant-1',
                        name: 'Sparda-Bank Nürnberg eG',
                        legal: 'Genossenschaftsbank',
                        domain: 'sb-nbg'
                    },
                    {
                        id: 'tenant-2',
                        name: 'Sparkasse Nürnberg',
                        legal: 'Institut des öffentlichen Rechts',
                        domain: 'spk-nbg'
                    }
                ];
                setAvailableTenants(mockTenants);
            } else {
                // Nutze die Mandanten direkt aus userData
                if (userData && userData.mandanten) {
                    const tenants = userData.mandanten.map(mandant => ({
                        id: `mandant-${mandant.mandant_id}`,
                        name: mandant.institut_name,
                        legal: mandant.legal || 'Nicht verfügbar',
                        domain: mandant.tenant_url,
                        mandant_id: mandant.mandant_id,
                        is_primary: mandant.is_primary,
                        access_level: mandant.access_level
                    }));

                    setAvailableTenants(tenants);

                    // Auto-select wenn nur ein Tenant oder primary tenant vorhanden
                    if (tenants.length === 1 && !selectedTenant) {
                        const primaryTenant = tenants[0];
                        setSelectedTenant(primaryTenant);
                        localStorage.setItem('selectedTenant', JSON.stringify(primaryTenant));
                    } else if (tenants.length > 1 && !selectedTenant) {
                        // Auto-select primary tenant
                        const primaryTenant = tenants.find(t => t.is_primary);
                        if (primaryTenant) {
                            setSelectedTenant(primaryTenant);
                            localStorage.setItem('selectedTenant', JSON.stringify(primaryTenant));
                        }
                    }
                } else {
                    console.warn('No mandanten found in userData');
                    setAvailableTenants([]);
                }
            }
        } catch (error) {
            console.error('Failed to load tenants:', error);
            setError('Mandanten konnten nicht geladen werden');
            setAvailableTenants([]);
        }
    };

    const login = async (credentials) => {
        try {
            setError(null);
            setLoading(true);

            //console.log('Starting login...'); // Debug
            const response = await authService.login(credentials);
            //console.log('Login response:', response); // Debug

            setStoredToken(response.token);
            setUser(response.user);
            await loadAvailableTenants(response.user);

            return { success: true };
        } catch (error) {
            console.error('Login error caught:', error); // Debug
            const errorMessage = error.message || 'Login fehlgeschlagen';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            //console.log('Login finished'); // Debug
            setLoading(false);
        }
    };

    const selectTenant = async (tenant) => {
        try {
            setLoading(true);

            // Bei echtem Backend könnte hier ein API-Call zur Tenant-Auswahl kommen
            const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true';

            if (USE_MOCK_API) {
                // Mock API Call
                await new Promise(resolve => setTimeout(resolve, 300));
            } else {
                // Hier könnte ein echter API-Call zum Backend kommen um den Tenant zu setzen
                // z.B. await api.post('/user/select-mandant', { mandant_id: tenant.mandant_id });
                console.log('Selecting tenant:', tenant);
            }

            setSelectedTenant(tenant);
            localStorage.setItem('selectedTenant', JSON.stringify(tenant));

            return { success: true };
        } catch (error) {
            console.error('Tenant selection failed:', error);
            setError('Mandanten-Auswahl fehlgeschlagen');
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            setLoading(true);
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
            // Auch bei Logout-Fehlern sollten wir den lokalen State clearen
        } finally {
            removeStoredToken();
            localStorage.removeItem('selectedTenant');
            setUser(null);
            setSelectedTenant(null);
            setAvailableTenants([]);
            setError(null);
            setLoading(false);
        }
    };

    const value = {
        user,
        selectedTenant,
        availableTenants,
        login,
        logout,
        selectTenant,
        loading,
        error,
        isAuthenticated: !!user,
        hasTenantSelected: !!selectedTenant,
        // Zusätzliche Utility-Funktionen
        refreshUser: checkAuth,
        clearError: () => setError(null),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};